/**
 * Pure classification of a microphone-acquisition failure into a UI status.
 *
 * No I/O: the permission state and the iframe Permissions-Policy result are gathered by
 * the (impure) hook layer and passed in, so this stays synchronous and unit-testable —
 * matching the ptt-state / mute-controller pure-module convention.
 *
 * The thing that throws is LiveKit's `localParticipant.setMicrophoneEnabled(true)`, which
 * may surface the underlying DOMException directly, wrap it on `.cause`, or only mention
 * the name in `.message`. `resolveMicErrorName` normalizes all three shapes.
 */

export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/**
 * - `ok`          : mic granted / working.
 * - `prompt`      : never asked, or the user dismissed the prompt (recoverable — the next
 *                   hold re-prompts). Silent, no message.
 * - `blocked`     : explicitly denied and sticky. Show the "enable in settings" guidance.
 * - `unavailable` : no mic / busy / insecure context / iframe Permissions-Policy block —
 *                   nothing the player can toggle, so just say voice is unavailable.
 */
export type MicStatus = 'ok' | 'prompt' | 'blocked' | 'unavailable';

const DOM_ERROR_NAMES = [
  'NotAllowedError',
  'NotFoundError',
  'NotReadableError',
  'AbortError',
  'SecurityError',
  'OverconstrainedError',
] as const;

type DomErrorName = (typeof DOM_ERROR_NAMES)[number];

function isDomErrorName(value: string): value is DomErrorName {
  return (DOM_ERROR_NAMES as readonly string[]).includes(value);
}

/**
 * Resolve the effective DOMException name from whatever `setMicrophoneEnabled` threw.
 * Walks `.name`, then `.cause?.name`, then a substring match on `.message` — so a wrapped
 * error (a real risk with livekit-client) still classifies correctly. Returns '' when no
 * recognizable error is present (used as the "success / no error" signal).
 */
export function resolveMicErrorName(err: unknown): string {
  if (!err || typeof err !== 'object') {
    return '';
  }
  const e = err as { name?: unknown; cause?: { name?: unknown } | null; message?: unknown };

  const direct = typeof e.name === 'string' ? e.name : '';
  if (isDomErrorName(direct)) {
    return direct;
  }

  const causeName = e.cause && typeof e.cause.name === 'string' ? e.cause.name : '';
  if (isDomErrorName(causeName)) {
    return causeName;
  }

  const message = typeof e.message === 'string' ? e.message : '';
  const fromMessage = DOM_ERROR_NAMES.find((name) => message.includes(name));
  if (fromMessage) {
    return fromMessage;
  }

  // Unknown/wrapped error: hand back whatever name we have so the classifier can still
  // fall back on permission state rather than mislabeling.
  return direct || causeName || '';
}

/**
 * Map (resolved error name, permission state, iframe-policy block) → MicStatus.
 *
 * Precedence is deliberate and state-first for the NotAllowed case: a sticky block requires
 * an explicit `denied`. A dismissed prompt — or a browser that cannot report state, e.g.
 * Firefox, which surfaces as `unsupported` — stays `prompt` so the next hold re-prompts.
 * Never strand a one-time dismiss as a permanent "blocked".
 */
export function classifyMicError(
  errorName: string,
  permissionState: MicPermissionState,
  policyBlocked: boolean,
): MicStatus {
  if (!errorName) {
    return 'ok';
  }

  // The iframe's Permissions-Policy disallows the mic (host forgot allow="microphone").
  // No prompt will ever appear and "enable in settings" would be wrong — it's the embed,
  // not a site permission. Treat as unavailable.
  if (policyBlocked) {
    return 'unavailable';
  }

  switch (errorName) {
    case 'NotFoundError': // no microphone hardware
    case 'NotReadableError': // OS/hardware error — often the mic is held by another app
    case 'AbortError':
    case 'SecurityError': // insecure context (non-HTTPS)
    case 'OverconstrainedError':
      return 'unavailable';

    case 'NotAllowedError':
    default:
      return permissionState === 'denied' ? 'blocked' : 'prompt';
  }
}
